import { useEffect } from 'react';

type Element = HTMLElement | Window | Document;
type Options = boolean | AddEventListenerOptions;

/*-------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
interface Params<T> {
  readonly eventName: string;
  onSignal?: (e: CustomEvent<T>) => void;
  readonly element?: Element;
  readonly options?: Options;
}

/*-------------------------------------------------------------------------------------------------
 * helpers
 * -----------------------------------------------------------------------------------------------*/

function dispatchCustomEvent<T>(
  eventName: string,
  data: T,
  element: Element = window,
) {
  const event = new CustomEvent(eventName, { detail: data });

  element.dispatchEvent(event);
}

/*-------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useEmitter = <T>(eventName: string, element: Element = window) => {
  const callEvent = (data: T) => {
    dispatchCustomEvent<T>(eventName, data, element);
  };

  return callEvent;
};

const useListener = <T>(
  eventName: string,
  onSignal: (e: CustomEvent<T>) => void,
  element: Element = window,
  options: Options = {},
) => {
  useEffect(() => {
    if (typeof onSignal === 'function') {
      const handleSignal = (e: Event) => {
        onSignal(e as CustomEvent);
      };

      element.addEventListener(eventName, handleSignal, options);

      return () =>
        element.removeEventListener(eventName, handleSignal, options);
    }
  }, [element, eventName, onSignal, options]);
};

const useCustomEvent = <T>({
  eventName,
  onSignal,
  element = window,
  options = {},
}: Params<T>) => {
  const handleSignal = onSignal || (() => null);

  useListener<T>(eventName, handleSignal, element, options);

  return useEmitter<T>(eventName, element);
};

export { dispatchCustomEvent, useCustomEvent, useEmitter, useListener };
